import { useState, useEffect } from 'react'
import { App } from 'antd'
import styled from 'styled-components'
import { AuthService } from '../../services/auth/authService'
import { TYPOGRAPHY, FONT_WEIGHT } from '../../styles/theme'

const StyledButton = styled.button<{ $loading: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 40px;
  padding: 0 16px;
  background: #fff;
  color: #3c4043;
  border: 1px solid #dadce0;
  border-radius: 6px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  font-family: inherit;
  cursor: ${({ $loading }) => ($loading ? 'not-allowed' : 'pointer')};
  opacity: ${({ $loading }) => ($loading ? 0.7 : 1)};
  transition:
    background 0.2s,
    box-shadow 0.2s;

  &:hover:not(:disabled) {
    background: #f8f9fa;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    background: #f1f3f4;
  }
`

const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid #dadce0;
  border-top-color: #0a66c2;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  flex-shrink: 0;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`

function LinkedInIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <path
        fill="#0A66C2"
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
      />
    </svg>
  )
}

interface LinkedInSignInButtonProps {
  label?: string
}

export function LinkedInSignInButton({
  label = 'Continue with LinkedIn',
}: LinkedInSignInButtonProps) {
  const [loading, setLoading] = useState(false)
  const { notification } = App.useApp()

  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setLoading(false)
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  const handleClick = async () => {
    if (loading) return
    setLoading(true)
    const { error } = await AuthService.signInWithLinkedIn()
    if (error) {
      setLoading(false)
      notification.error({
        title: 'LinkedIn Sign-In Failed',
        description: error.message,
        placement: 'top',
      })
    }
    // On success the browser redirects to LinkedIn — loading stays true intentionally
  }

  return (
    <StyledButton onClick={handleClick} $loading={loading} disabled={loading}>
      {loading ? <Spinner /> : <LinkedInIcon />}
      {label}
    </StyledButton>
  )
}
